<template>
  <div
    class="absolute transition-all duration-300"
    :style="{
      top: availability.position.top,
      left: availability.position.left
    }"
  >
    <div
      class="border border-l-4 rounded-lg bg-pure-white px-3 flex flex-col cursor-pointer shadow-md overflow-y-hidden transition-all duration-300"
      :class="[isHasTwoSpaces ? 'py-2 gap-1' : 'py-0 gap-0 justify-center']"
      :style="{
        height: availability.element.height,
        width: availability.element.width,
        borderColor: availability.element.borderColor
      }"
      @click="editOpened = true"
    >
      <div v-if="availability.is_overlapped && isHasThreeSpaces">
        <BlIcon icon="warning" color="tomato-7" class="h-6 w-6" />
      </div>
      <div
        v-else-if="isHasTwoSpaces"
        class="h-7 w-7 flex-shrink-0 rounded-full grid place-items-center"
        :style="{ background: availability.secondary_color }"
      >
        <BlIcon v-if="isOpenRoom" icon="door" class="h-4 w-4" />
        <BlIcon v-else-if="slotType === 'therapist'" icon="door" class="h-4 w-4" />
        <BlIcon v-else-if="slotType === 'room'" icon="user" class="h-4 w-4" />
      </div>
      <div class="flex" :title="availability.title.name">
        <BlTextBody2
          :text="availability.title.name"
          class="text-slate-800 line-clamp-2"
        />
      </div>
      <div
        v-if="
          enableBranch &&
            slotType === 'therapist' &&
            availability.room.branch &&
            isHasTwoSpaces
        "
        class="flex"
      >
        <div
          class="h-7 max-w-36 px-2 flex items-center justify-center rounded"
          :style="{
            backgroundColor:
              availability.room.branch.background_color || 'unset'
          }"
          :title="availability.room.branch.name"
        >
          <BlTextBody2
            class="truncate"
            :text="availability.room.branch.name || '-'"
            :style="{ color: availability.room.branch.color || 'unset' }"
          />
        </div>
      </div>
      <div v-if="availability.is_overlapped">
        <BlTextSubtitle3
          v-if="isHasThreeSpaces"
          text="Overlapping availabilities detected in this slot."
          class="text-tomato-700"
        />
        <BlTextSubtitle3
          v-else
          text="Overlapping slot."
          class="text-tomato-700"
        />
      </div>
    </div>

    <EditAvailabilitiesModal
      v-if="editOpened"
      :availabilityId="availability.id"
      :availabilityDate="availability.date"
      @close-modal="editOpened = false"
      @after-commit="$emit('after-commit')"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { CLOCK_ROUND } from "@/plugins/assets.js";

import EditAvailabilitiesModal from "./EditAvailabilitiesModal.vue";

import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlIcon from "../../BlIcon.vue";

export default {
  name: "AvailabilitiesSlot",
  components: {
    EditAvailabilitiesModal,
    BlTextSubtitle3,
    BlTextBody2,
    BlIcon
  },
  props: {
    isCompact: { type: Boolean, default: false },
    item: { type: Object, required: true },
    headers: { type: Array, required: true },
    slotHeight: { type: Number, required: true }, // in rem
    slotWidth: { type: String, required: true }
    // @after-commit
  },
  data() {
    return {
      editOpened: false,
      clockRound: CLOCK_ROUND
    };
  },
  computed: {
    ...mapGetters(["currentUser", "currentCenter"]),
    enableBranch() {
      return this.currentCenter.enable_branch;
    },
    slotType() {
      // room, therapist
      return this.$route.params.tab || "room";
    },
    isOpenRoom() {
      return !this.item.user_id;
    },
    isHasTwoSpaces() {
      const slot = this.getSlotCount(
        this.item.start_time_string,
        this.item.end_time_string
      );
      return !this.isCompact || slot > 1;
    },
    isHasThreeSpaces() {
      const slot = this.getSlotCount(
        this.item.start_time_string,
        this.item.end_time_string
      );
      return !this.isCompact || slot > 2;
    },
    availability() {
      const keyHeaders = this.headers.map(i => i.value);

      const item = { ...this.item };
      const position = {
        top: "calc(",
        left: "calc(5rem"
      };
      const element = {
        height: ``,
        width: `calc(${this.slotWidth} - 2px)`,
        borderColor: item.primary_color
      };

      const indexOfTop = this.clockRound.findIndex(i => {
        const dt = i.split(":")[0];
        return dt === item.start_time_string.split(":")[0];
      });
      position.top += indexOfTop * (this.slotHeight * 2) + "rem";
      if (item.start_time_string.split(":")[1] === "30") {
        position.top += ` + ${this.slotHeight}rem`;
      }
      position.top += " + 0.5px)";

      const indexOfLeft = keyHeaders.findIndex(i => i === item.key.id);
      if (indexOfLeft > -1) {
        const loop = indexOfLeft;
        for (let index = 0; index < loop; index++) {
          position.left += ` + ${this.slotWidth}`;
        }
      }
      position.left += " + 0.5px)";

      const calculatedHeight =
        this.getSlotCount(item.start_time_string, item.end_time_string) *
        this.slotHeight;
      element.height = `calc(${calculatedHeight}rem - 2px)`;

      let title = { name: "Open room" };
      if (this.slotType === "therapist" && !this.isOpenRoom) {
        title = item.room;
      }
      if (this.slotType === "room" && !this.isOpenRoom) {
        title = item.user;
      }
      return { ...item, position, element, title };
    }
  },
  mounted() {},
  methods: {
    getSlotCount(start, end) {
      const s = start.split(":").map(i => Number(i));
      const e = end.split(":").map(i => Number(i));
      let slots = 0;
      slots += (e[0] - s[0]) * 2;
      if (s[1] === 30) slots -= 1;
      if (e[1] === 30) slots += 1;
      return slots;
    },
    onOpenSlot({ dimension, time, header }) {
      if (dimension === "daily") {
      }
      if (dimension === "weekly") {
      }
      this.editOpened = true;
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/availabilities/timeslot/Slot.vue